import { emotionDescription } from "@/common/constants/descriptions";
import { getSortedPrimaryTopics } from "@/services/events/topics.service";
import lodashOrderBy from "lodash/orderBy";

import SentimentChart from "@/components/common/details/SentimentChart.vue";
import LineGraph from "@/components/events/LineGraph.vue";
import VerifiedEventsPieGraph from "@/components/events/VerifiedEventsPieGraph.vue";
import VerifiedEventsBarGraph from "@/components/events/VerifiedEventsBarGraph.vue";
import {
  convertTimeSeriesToAggregatedArray,
  formatAggregatedDataForBarChart,
  convertBarGraphDataToAggregatedArray,
} from "@/services/charts/charts.service";

export const CHART_COLORS = [
  "#78B9CB",
  "#684080",
  "#B24382",
  "#D16562",
  "#0A2240",
  "#432852",
  "#CDDB00",
  "#565656",
  "#333333",
  "#8EC2CD",
  "#68B9CB",
  "#358797",
  "#464E75",
  "#57467A",
];

export const CHART_TOOLTIP = {
  backgroundColor: "rgba(0,0,0,0.8)",
  borderColor: "rgba(255,255,255,0.1)",
  borderWidth: 1,
  padding: 10,
  textStyle: {
    color: "rgba(255,255,255,1)",
  },
};

export const CHART_TYPE_SELECTION = {
  lineGraph: {
    id: "lineGraph",
    text: "View as Line Chart (by day)",
    chartComponent: LineGraph,
  },
  pieGraph: {
    id: "pieGraph",
    text: "View as Pie Chart (aggregated)",
    chartComponent: VerifiedEventsPieGraph,
  },
  barGraph: {
    id: "barGraph",
    text: "View as Bar Chart (aggregated)",
    chartComponent: VerifiedEventsBarGraph,
  },
  sentimentChart: {
    id: "sentimentChart",
    text: "Sentiment Chart",
    chartComponent: SentimentChart,
  },
};

const SECURITY_CHART_CONFIG = [
  {
    title: "Verified Events By Category",
    chartDataElement: "topicsByDate",
    filterSelection: "topics",
    createConfigFunction: createTimeSeriesConfig,
  },
  {
    title: "Verified Events By Sector",
    chartDataElement: "sectorTypes",
    createConfigFunction: createPieGraphConfig,
  },
  {
    title: "Verified Events By Attack Type",
    chartDataElement: "attackTypes",
    createConfigFunction: createBarGraphConfig,
  },
  {
    title: "Verified Events By Perpetrator",
    chartDataElement: "perpetratorTypes",
    createConfigFunction: createBarGraphConfig,
  },
];

const MARITIME_CHART_CONFIG = [
  {
    title: "Verified Events By Category",
    chartDataElement: "topicsByDate",
    filterSelection: "topics",
    createConfigFunction: createTimeSeriesConfig,
  },
  {
    title: "Verified Events By Vessel Type",
    chartDataElement: "vesselTypes",
    chartComponent: VerifiedEventsPieGraph,
    createConfigFunction: createPieGraphConfig,
  },
  {
    title: "Verified Events By Time of Day",
    chartDataElement: "hourOfDay",
    createConfigFunction: createBarGraphConfig,
  },
];

const DETECTED_CHART_CONFIG = [
  {
    title: "Events By Category",
    chartDataElement: "topicsByDate",
    filterSelection: "topics",
    createConfigFunction: createTimeSeriesConfig,
  },
  {
    title: "Events AI Sentiment",
    chartDataElement: "sentimentHistogram",
    chartComponent: SentimentChart,
    createConfigFunction: createSentimentConfig,
  },
  {
    title: "Events AI Emotions",
    chartDataElement: "emotionsByDate",
    filterSelection: "emotions",
    description: emotionDescription,
    createConfigFunction: createTimeSeriesConfig,
  },
  {
    title: "Source Metadata: Source Type",
    featureLock: "livesearch-source-metadata-type", //TODO CUBLER
    chartDataElement: "sourceTypeHistogram",
    filterSelection: "sourceMetadataType",
    createConfigFunction: createTimeSeriesConfig,
  },
  {
    title: "Source Metadata: Reliability",
    featureLock: "livesearch-source-metadata-reliability",
    chartDataElement: "reliabilityHistogram",
    filterSelection: "sourceMetadataReliability",
    createConfigFunction: createTimeSeriesConfig,
  },
  {
    title: "Source Metadata: Social Authenticity",
    featureLock: "livesearch-source-metadata-social-authenticity",
    chartDataElement: "authenticityHistogram",
    filterSelection: "sourceMetadataSocialAuthenticity",
    createConfigFunction: createTimeSeriesConfig,
  },
  {
    title: "Source Metadata: Social Followers",
    featureLock: "livesearch-source-metadata-social-follower-count",
    chartDataElement: "followersHistogram",
    filterSelection: "sourceMetadataFollowerCountBracket",
    createConfigFunction: createTimeSeriesConfig,
  },
];

export const CHART_CONFIGS = {
  all: DETECTED_CHART_CONFIG.map((config) =>
    config.createConfigFunction(config)
  ),
  detected: DETECTED_CHART_CONFIG.map((config) =>
    config.createConfigFunction(config)
  ),
  hotspots: DETECTED_CHART_CONFIG.map((config) =>
    config.createConfigFunction(config)
  ),
  maritime: MARITIME_CHART_CONFIG.map((config) =>
    config.createConfigFunction(config)
  ),
  security: SECURITY_CHART_CONFIG.map((config) =>
    config.createConfigFunction(config)
  ),
};

function createPieGraphConfig({ title, chartDataElement }) {
  return {
    title,
    chartDataElement,
    defaultChartType: CHART_TYPE_SELECTION.pieGraph.id,
    chartTypeConfigs: {
      [CHART_TYPE_SELECTION.pieGraph.id]: {
        getChartData: (chartsData) => chartsData?.[chartDataElement],
      },
      [CHART_TYPE_SELECTION.barGraph.id]: {
        getChartData: (chartsData) =>
          formatAggregatedDataForBarChart(chartsData?.[chartDataElement]),
        props: {
          rotation: "90",
        },
      },
    },
  };
}

function createBarGraphConfig({ title, chartDataElement }) {
  return {
    title,
    chartDataElement,
    defaultChartType: CHART_TYPE_SELECTION.barGraph.id,
    chartTypeConfigs: {
      [CHART_TYPE_SELECTION.pieGraph.id]: {
        getChartData: (chartsData) =>
          convertBarGraphDataToAggregatedArray(chartsData?.[chartDataElement]),
      },
      [CHART_TYPE_SELECTION.barGraph.id]: {
        getChartData: (chartsData) => chartsData?.[chartDataElement],
        props: {
          rotation: "90",
        },
      },
    },
  };
}

function createSentimentConfig({
  title,
  chartDataElement,
  filterSelection,
  description,
}) {
  return {
    title,
    chartDataElement,
    filterSelection,
    description,
    defaultChartType: CHART_TYPE_SELECTION.sentimentChart.id,
    chartTypeConfigs: {
      [CHART_TYPE_SELECTION.sentimentChart.id]: {
        getChartData: (chartsData) => chartsData?.[chartDataElement],
      },
    },
  };
}

function createTimeSeriesConfig({
  title,
  chartDataElement,
  filterSelection,
  description,
}) {
  return {
    title,
    chartDataElement,
    filterSelection,
    description,
    defaultChartType: CHART_TYPE_SELECTION.lineGraph.id,
    chartTypeConfigs: {
      [CHART_TYPE_SELECTION.lineGraph.id]: {
        getChartData: (chartsData) => chartsData?.[chartDataElement],
      },
      [CHART_TYPE_SELECTION.pieGraph.id]: {
        getChartData: (chartsData) =>
          convertTimeSeriesToAggregatedArray(
            chartsData?.[chartDataElement]?.seriesData
          ),
      },
      [CHART_TYPE_SELECTION.barGraph.id]: {
        getChartData: (chartsData) =>
          formatAggregatedDataForBarChart(
            convertTimeSeriesToAggregatedArray(
              chartsData?.[chartDataElement]?.seriesData
            )
          ),
        props: {
          rotation: "90",
        },
      },
    },
  };
}
